import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { ArrowLeft, ArrowRight, Plus, X } from 'lucide-react';
import { fetchShipments, createShipment, suggestHsCode } from '../services/shipments';
import ShipmentCard from '../components/shipments/ShipmentCard';

const TRADE_FLOW_OPTIONS = [
  { value: 'import', label: 'Import' },
  { value: 'export', label: 'Export' },
];

const PRODUCT_USE_OPTIONS = [
  { value: 'food_ingredient', label: 'Food ingredient' },
  { value: 'food_supplement', label: 'Food supplement' },
  { value: 'cosmetic_ingredient', label: 'Cosmetic ingredient' },
  { value: 'animal_feed', label: 'Animal feed' },
  { value: 'other', label: 'Other' },
];

const ORGANIC_OPTIONS = [
  { value: 'yes', label: 'Yes' },
  { value: 'no', label: 'No' },
  { value: 'unknown', label: 'Not sure' },
];

const TRANSPORT_MODE_OPTIONS = [
  { value: 'sea_freight', label: 'Sea freight' },
  { value: 'air_freight', label: 'Air freight' },
  { value: 'road', label: 'Road' },
  { value: 'rail', label: 'Rail' },
  { value: 'courier', label: 'Courier' },
];

const QUANTITY_UNIT_OPTIONS = [
  { value: 'kg', label: 'kg' },
  { value: 'g', label: 'g' },
  { value: 'ton', label: 'ton' },
  { value: 'litre', label: 'litre' },
  { value: 'unit', label: 'unit' },
];

function createReference() {
  return `CTQ-2026-${String(Math.floor(Math.random() * 9000) + 1000)}`;
}

function createProductId() {
  return `product-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
}

function createEmptyProduct() {
  return {
    id: createProductId(),
    product_name: '',
    product_use: '',
    organic: '',
    quantity_value: '',
    quantity_unit: '',
    hs_code: '',
    hs_code_confirmed: null,
    hs_code_source: null,
    hs_code_confidence: null,
    hs_code_needs_review: true,
    selectedHsCandidate: null,
    selectedHsSuggestionId: null,
    hsSuggestionInputSnapshot: null,
    hsSuggestion: null,
    hsSuggestionLoading: false,
    hsSuggestionError: '',
  };
}

function createInitialShipmentForm() {
  return {
    reference: createReference(),
    trade_flow: '',
    origin_country: '',
    destination_country: '',
    transport_mode: '',
    incoterm: '',
  };
}

function validateShipmentDetails(form) {
  const nextErrors = {};
  const requiredFields = [
    'reference',
    'trade_flow',
    'origin_country',
    'destination_country',
    'transport_mode',
    'incoterm',
  ];

  requiredFields.forEach((key) => {
    if (!String(form[key] ?? '').trim()) {
      nextErrors[key] = 'This field is required.';
    }
  });

  return nextErrors;
}

function validateProduct(product) {
  const nextErrors = {};

  if (!String(product.product_name ?? '').trim()) {
    nextErrors.product_name = 'Product name is required.';
  }
  if (!String(product.product_use ?? '').trim()) {
    nextErrors.product_use = 'Product use is required.';
  }
  if (!String(product.organic ?? '').trim()) {
    nextErrors.organic = 'Organic status is required.';
  }
  if (!String(product.quantity_unit ?? '').trim()) {
    nextErrors.quantity_unit = 'Quantity unit is required.';
  }

  const quantityValue = Number(product.quantity_value);
  if (
    String(product.quantity_value ?? '').trim() === ''
    || Number.isNaN(quantityValue)
    || quantityValue <= 0
  ) {
    nextErrors.quantity_value = 'Enter a positive number.';
  }

  return nextErrors;
}

function validateProducts(products) {
  if (!Array.isArray(products) || products.length === 0) {
    return {
      listError: 'Add at least one product before creating the shipment.',
      itemErrors: [],
    };
  }

  const itemErrors = products.map(validateProduct);
  return {
    listError: '',
    itemErrors,
  };
}

function buildShipmentPayload(shipmentDetails, products) {
  return {
    reference: shipmentDetails.reference.trim(),
    trade_flow: shipmentDetails.trade_flow,
    origin_country: shipmentDetails.origin_country.trim(),
    destination_country: shipmentDetails.destination_country.trim(),
    transport_mode: shipmentDetails.transport_mode,
    incoterm: shipmentDetails.incoterm.trim(),
    products: products.map((product) => {
      const hsCode = product.hs_code.trim();
      const suggestionInputSnapshot = product.hsSuggestionInputSnapshot || buildHsSuggestionPayload(shipmentDetails, product);
      const hasAcceptedSuggestionSnapshot = Boolean(product.selectedHsCandidate && hsCode);

      return {
        product_name: product.product_name.trim(),
        product_use: product.product_use,
        organic: product.organic,
        quantity_value: Number(product.quantity_value),
        quantity_unit: product.quantity_unit,
        hs_code: hsCode || undefined,
        hs_code_confirmed: hsCode ? (product.hs_code_confirmed || hsCode) : undefined,
        hs_code_source: hsCode ? (product.hs_code_source || undefined) : undefined,
        hs_code_confidence: hsCode ? (product.hs_code_confidence ?? null) : undefined,
        hs_code_needs_review: hsCode ? product.hs_code_needs_review ?? true : undefined,
        hs_suggestion_snapshot: hasAcceptedSuggestionSnapshot
          ? {
            input_snapshot: suggestionInputSnapshot,
            candidates: product.hsSuggestion?.candidates || [],
            clarification_questions: product.hsSuggestion?.clarification_questions || [],
            disclaimer: product.hsSuggestion?.disclaimer || null,
            selected_candidate: product.selectedHsCandidate,
            selected_hs_code: product.selectedHsCandidate.hs_code,
            agent_name: product.hsSuggestion?.agent || 'hs_code_suggestion_agent',
            status: 'accepted',
            user_confirmed: true,
          }
          : undefined,
      };
    }),
  };
}

function buildHsSuggestionPayload(shipmentDetails, product) {
  const quantityValue = String(product.quantity_value ?? '').trim();
  return {
    shipment_context: {
      trade_flow: shipmentDetails.trade_flow,
      origin_country: shipmentDetails.origin_country.trim(),
      destination_country: shipmentDetails.destination_country.trim(),
      transport_mode: shipmentDetails.transport_mode || undefined,
      incoterm: shipmentDetails.incoterm.trim() || undefined,
    },
    product: {
      product_name: product.product_name.trim(),
      product_use: product.product_use || undefined,
      organic: product.organic === 'yes' ? true : product.organic === 'no' ? false : null,
      quantity_value: quantityValue ? Number(product.quantity_value) : null,
      quantity_unit: product.quantity_unit || undefined,
      hs_code_user_input: product.hs_code.trim() || undefined,
    },
  };
}

function getApiErrorMessage(err) {
  const detail = err?.response?.data?.detail;
  if (typeof detail === 'string' && detail.trim()) {
    return detail;
  }
  if (Array.isArray(detail) && detail.length > 0) {
    return detail
      .map((item) => item?.msg || item?.message || 'Invalid request')
      .join(' ');
  }
  return err?.message || 'We could not create the shipment. Please try again.';
}

export default function Shipments() {
  const navigate = useNavigate();
  const [shipments, setShipments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchParams, setSearchParams] = useSearchParams();
  const [showNew, setShowNew] = useState(searchParams.get('new') === '1');

  const reload = () => {
    setLoading(true);
    fetchShipments()
      .then((data) => setShipments(Array.isArray(data) ? data : []))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    reload();
  }, []);

  const shipmentList = Array.isArray(shipments) ? shipments : [];

  const closeModal = () => {
    setShowNew(false);
    if (searchParams.get('new')) {
      const nextParams = new URLSearchParams(searchParams);
      nextParams.delete('new');
      setSearchParams(nextParams);
    }
  };

  const handleCreated = (createdShipment) => {
    closeModal();
    navigate(`/shipments/${createdShipment.shipment_id}/preparing-checklist`);
  };

  return (
    <div className="space-y-6" data-testid="shipments-page">
      <div className="flex items-center justify-between">
        <p className="text-sm text-slate-400">
          {shipmentList.length} shipment{shipmentList.length === 1 ? '' : 's'} registered
        </p>
        <button
          onClick={() => setShowNew(true)}
          className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-cyan-glow text-navy-950 font-semibold hover:bg-cyan-hover transition-colors"
          data-testid="shipments-new-btn"
        >
          <Plus className="h-4 w-4" />
          New shipment
        </button>
      </div>

      {loading ? (
        <div className="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-3">
          {[0, 1, 2, 3, 4, 5].map((i) => (
            <div key={i} className="h-44 rounded-xl bg-navy-900 border border-white/10 animate-pulse" />
          ))}
        </div>
      ) : (
        <div className="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-3">
          {shipmentList.map((shipment) => (
            <ShipmentCard key={shipment.id} shipment={shipment} />
          ))}
        </div>
      )}

      {showNew ? <NewShipmentModal onClose={closeModal} onCreated={handleCreated} /> : null}
    </div>
  );
}

function NewShipmentModal({ onClose, onCreated }) {
  const [step, setStep] = useState(1);
  const [shipmentDetails, setShipmentDetails] = useState(createInitialShipmentForm);
  const [products, setProducts] = useState([createEmptyProduct()]);
  const [shipmentErrors, setShipmentErrors] = useState({});
  const [productErrors, setProductErrors] = useState([{}]);
  const [productsError, setProductsError] = useState('');
  const [submitError, setSubmitError] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const updateShipmentField = (key) => (event) => {
    const nextValue = event.target.value;
    setShipmentDetails((current) => ({ ...current, [key]: nextValue }));
    setShipmentErrors((current) => {
      if (!current[key]) {
        return current;
      }
      const nextErrors = { ...current };
      delete nextErrors[key];
      return nextErrors;
    });
  };

  const updateProductField = (productId, key) => (event) => {
    const nextValue = event.target.value;
    setProducts((current) => current.map((product) => (
      product.id === productId
        ? { ...product, [key]: nextValue, hsSuggestionError: '' }
        : product
    )));
    setProductErrors((current) => current.map((errors, index) => {
      if (products[index]?.id !== productId || !errors?.[key]) {
        return errors;
      }
      const nextErrors = { ...errors };
      delete nextErrors[key];
      return nextErrors;
    }));
    setProductsError('');
  };

  const updateProductHsCode = (productId) => (event) => {
    const nextValue = event.target.value;
    const trimmedValue = nextValue.trim();

    setProducts((current) => current.map((product) => (
      product.id === productId
        ? {
          ...product,
          hs_code: nextValue,
          hs_code_confirmed: trimmedValue || null,
          hs_code_source: trimmedValue ? 'user_manual' : null,
          hs_code_confidence: null,
          hs_code_needs_review: true,
          selectedHsCandidate: null,
          selectedHsSuggestionId: null,
          hsSuggestionError: '',
        }
        : product
    )));
    setProductErrors((current) => current.map((errors, index) => {
      if (products[index]?.id !== productId || !errors?.hs_code) {
        return errors;
      }
      const nextErrors = { ...errors };
      delete nextErrors.hs_code;
      return nextErrors;
    }));
    setProductsError('');
  };

  const selectHsSuggestionCandidate = (productId, candidate) => {
    setProducts((current) => current.map((product) => (
      product.id === productId
        ? {
          ...product,
          hs_code: candidate.hs_code,
          hs_code_confirmed: candidate.hs_code,
          hs_code_source: 'ai_suggested_user_confirmed',
          hs_code_confidence: candidate.confidence,
          hs_code_needs_review: false,
          selectedHsCandidate: candidate,
          selectedHsSuggestionId: product.hsSuggestion?.suggestion_id || null,
          hsSuggestionError: '',
        }
        : product
    )));
    setProductsError('');
  };

  const requestHsSuggestion = async (productId) => {
    const product = products.find((item) => item.id === productId);
    if (!product || !String(product.product_name ?? '').trim()) {
      return;
    }
    const requestPayload = buildHsSuggestionPayload(shipmentDetails, product);

    setProducts((current) => current.map((item) => (
      item.id === productId
        ? {
          ...item,
          hsSuggestionInputSnapshot: requestPayload,
          hsSuggestionLoading: true,
          hsSuggestionError: '',
        }
        : item
    )));

    try {
      const response = await suggestHsCode(requestPayload);
      setProducts((current) => current.map((item) => (
        item.id === productId
          ? {
            ...item,
            hsSuggestion: response,
            hsSuggestionLoading: false,
            hsSuggestionError: '',
          }
          : item
      )));
    } catch (err) {
      const message = err?.response?.data?.detail?.message
        || err?.response?.data?.detail
        || err?.message
        || 'We could not suggest HS codes right now.';
      setProducts((current) => current.map((item) => (
        item.id === productId
          ? {
            ...item,
            hsSuggestionLoading: false,
            hsSuggestionError: String(message),
          }
          : item
      )));
    }
  };

  const addProduct = () => {
    setProducts((current) => [...current, createEmptyProduct()]);
    setProductErrors((current) => [...current, {}]);
    setProductsError('');
  };

  const removeProduct = (productId) => {
    if (products.length === 1) {
      return;
    }

    const productIndex = products.findIndex((product) => product.id === productId);
    setProducts((current) => current.filter((product) => product.id !== productId));
    setProductErrors((current) => current.filter((_, index) => index !== productIndex));
  };

  const goToProductsStep = () => {
    const nextErrors = validateShipmentDetails(shipmentDetails);
    setShipmentErrors(nextErrors);

    if (Object.keys(nextErrors).length > 0) {
      setSubmitError('Please complete the shipment details before continuing.');
      return;
    }

    setSubmitError('');
    setStep(2);
  };

  const submit = async (event) => {
    event.preventDefault();

    const nextShipmentErrors = validateShipmentDetails(shipmentDetails);
    const nextProductsValidation = validateProducts(products);
    const hasProductErrors = nextProductsValidation.itemErrors.some(
      (errors) => Object.keys(errors).length > 0,
    );

    setShipmentErrors(nextShipmentErrors);
    setProductErrors(nextProductsValidation.itemErrors.length > 0 ? nextProductsValidation.itemErrors : [{}]);
    setProductsError(nextProductsValidation.listError);

    if (Object.keys(nextShipmentErrors).length > 0) {
      setStep(1);
      setSubmitError('Please complete the shipment details before continuing.');
      return;
    }

    if (nextProductsValidation.listError || hasProductErrors) {
      setStep(2);
      setSubmitError('Please correct the highlighted product details.');
      return;
    }

    setSubmitting(true);
    setSubmitError('');

    try {
      const createdShipment = await createShipment(buildShipmentPayload(shipmentDetails, products));
      onCreated(createdShipment);
    } catch (err) {
      setSubmitError(getApiErrorMessage(err));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 grid place-items-center px-4 bg-black/60 backdrop-blur-sm" data-testid="new-shipment-modal">
      <div className="max-h-[calc(100vh-2rem)] w-full max-w-4xl overflow-y-auto rounded-2xl border border-white/10 bg-navy-900 p-7 shadow-2xl">
        <div className="flex items-start justify-between gap-4 mb-6">
          <div>
            <h3 className="font-display text-2xl font-bold tracking-tight">Register a shipment</h3>
            <p className="text-sm text-slate-400 mt-1">
              {step === 1 ? 'Step 1 of 2: Shipment details' : 'Step 2 of 2: Products'}
            </p>
            <ProgressHeader step={step} />
          </div>
          <button onClick={onClose} className="p-1.5 rounded-md text-slate-400 hover:bg-white/5" data-testid="new-shipment-close">
            <X className="h-5 w-5" />
          </button>
        </div>

        {submitError ? (
          <div className="mb-5 rounded-lg border border-red-500/20 bg-red-500/10 p-3 text-sm text-red-300">
            {submitError}
          </div>
        ) : null}

        <form onSubmit={submit} data-testid="new-shipment-form">
          {step === 1 ? (
            <ShipmentDetailsStep
              form={shipmentDetails}
              errors={shipmentErrors}
              onChange={updateShipmentField}
            />
          ) : (
            <ProductsStep
              products={products}
              productErrors={productErrors}
              productsError={productsError}
              onChange={updateProductField}
              onHsCodeChange={updateProductHsCode}
              onAddProduct={addProduct}
              onRemoveProduct={removeProduct}
              onSuggestHsCode={requestHsSuggestion}
              onSelectHsCandidate={selectHsSuggestionCandidate}
            />
          )}

          <div className="mt-8 flex flex-col gap-3 border-t border-white/10 pt-5 sm:flex-row sm:items-center sm:justify-between">
            <button
              type="button"
              onClick={step === 1 ? onClose : () => { setSubmitError(''); setStep(1); }}
              className="inline-flex items-center justify-center gap-2 rounded-lg border border-white/10 px-5 py-2.5 text-slate-300 hover:bg-white/5"
            >
              {step === 1 ? (
                'Cancel'
              ) : (
                <>
                  <ArrowLeft className="h-4 w-4" />
                  Back
                </>
              )}
            </button>

            {step === 1 ? (
              <button
                type="button"
                onClick={goToProductsStep}
                className="inline-flex items-center justify-center gap-2 rounded-lg bg-cyan-glow px-5 py-2.5 font-semibold text-navy-950 hover:bg-cyan-hover"
                data-testid="ns-next-btn"
              >
                Next: Add products
                <ArrowRight className="h-4 w-4" />
              </button>
            ) : (
              <button
                type="submit"
                disabled={submitting}
                className="inline-flex items-center justify-center gap-2 rounded-lg bg-cyan-glow px-5 py-2.5 font-semibold text-navy-950 hover:bg-cyan-hover disabled:opacity-60"
                data-testid="ns-submit-btn"
              >
                {submitting ? 'Creating shipment...' : 'Create shipment'}
              </button>
            )}
          </div>
        </form>
      </div>
    </div>
  );
}

function ProgressHeader({ step }) {
  return (
    <div className="mt-4 flex items-center gap-3">
      <ProgressNode number={1} label="Shipment details" active={step === 1} complete={step > 1} />
      <div className={`h-px flex-1 max-w-16 ${step > 1 ? 'bg-cyan-glow/60' : 'bg-white/10'}`} />
      <ProgressNode number={2} label="Products" active={step === 2} complete={false} />
    </div>
  );
}

function ProgressNode({ number, label, active, complete }) {
  return (
    <div className="flex items-center gap-2">
      <span
        className={`grid h-7 w-7 place-items-center rounded-full border text-xs font-semibold ${
          active || complete
            ? 'border-cyan-glow/40 bg-cyan-glow/10 text-cyan-glow'
            : 'border-white/10 bg-navy-950 text-slate-500'
        }`}
      >
        {number}
      </span>
      <span className={`text-xs font-mono uppercase tracking-[0.25em] ${active || complete ? 'text-cyan-glow' : 'text-slate-500'}`}>
        {label}
      </span>
    </div>
  );
}

function ShipmentDetailsStep({ form, errors, onChange }) {
  return (
    <div className="grid grid-cols-1 gap-5 md:grid-cols-2" data-testid="shipment-details-step">
      <Field
        label="Reference"
        value={form.reference}
        onChange={onChange('reference')}
        error={errors.reference}
        testid="ns-reference"
      />
      <Select
        label="Trade flow"
        value={form.trade_flow}
        onChange={onChange('trade_flow')}
        options={TRADE_FLOW_OPTIONS}
        placeholder="Select trade flow"
        error={errors.trade_flow}
        testid="ns-trade-flow"
      />

      <Field
        label="Origin country"
        value={form.origin_country}
        onChange={onChange('origin_country')}
        error={errors.origin_country}
        testid="ns-origin"
      />
      <Field
        label="Destination country"
        value={form.destination_country}
        onChange={onChange('destination_country')}
        error={errors.destination_country}
        testid="ns-destination"
      />

      <Select
        label="Transport mode"
        value={form.transport_mode}
        onChange={onChange('transport_mode')}
        options={TRANSPORT_MODE_OPTIONS}
        placeholder="Select transport mode"
        error={errors.transport_mode}
        testid="ns-transport-mode"
      />
      <Field
        label="Incoterm"
        value={form.incoterm}
        onChange={onChange('incoterm')}
        error={errors.incoterm}
        testid="ns-incoterm"
      />
    </div>
  );
}

function ProductsStep({
  products,
  productErrors,
  productsError,
  onChange,
  onHsCodeChange,
  onAddProduct,
  onRemoveProduct,
  onSuggestHsCode,
  onSelectHsCandidate,
}) {
  return (
    <div className="space-y-5" data-testid="shipment-products-step">
      <div className="flex flex-col gap-3 rounded-xl border border-white/10 bg-navy-950/30 p-4 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <div className="text-xs font-mono uppercase tracking-[0.25em] text-cyan-glow">Products</div>
          <p className="mt-1 text-sm text-slate-400">
            Add every product included in this shipment. Product details will be saved as a structured products list.
          </p>
        </div>
        <button
          type="button"
          onClick={onAddProduct}
          className="inline-flex items-center justify-center gap-2 rounded-lg border border-cyan-glow/30 px-4 py-2 text-sm font-semibold text-cyan-glow hover:bg-cyan-glow/10"
          data-testid="ns-add-product-btn"
        >
          <Plus className="h-4 w-4" />
          Add another product
        </button>
      </div>

      {productsError ? (
        <div className="rounded-lg border border-red-500/20 bg-red-500/10 p-3 text-sm text-red-300">
          {productsError}
        </div>
      ) : null}

      <div className="space-y-4">
        {products.map((product, index) => (
          <ProductCard
            key={product.id}
            product={product}
            index={index}
            errors={productErrors[index] || {}}
            canRemove={products.length > 1}
            onChange={onChange}
            onHsCodeChange={onHsCodeChange}
            onRemove={onRemoveProduct}
            onSuggestHsCode={onSuggestHsCode}
            onSelectHsCandidate={onSelectHsCandidate}
          />
        ))}
      </div>
    </div>
  );
}

function ProductCard({
  product,
  index,
  errors,
  canRemove,
  onChange,
  onHsCodeChange,
  onRemove,
  onSuggestHsCode,
  onSelectHsCandidate,
}) {
  const suggestionDisclaimer = product.hsSuggestion?.disclaimer
    || 'AI-suggested HS codes are advisory only and require user confirmation.';
  const suggestionStatus = product.hsSuggestionLoading
    ? 'Suggesting HS codes...'
    : product.hsSuggestionError
      ? product.hsSuggestionError
      : product.hsSuggestion?.candidates?.length > 0
        ? 'HS code suggestions ready.'
      : product.hsSuggestion?.needs_clarification
          ? 'More product details may be needed.'
          : '';
  const selectedCandidateHsCode = product.selectedHsCandidate?.hs_code || null;

  return (
    <div className="rounded-xl border border-white/10 bg-navy-950/50 p-5">
      <div className="mb-4 flex items-start justify-between gap-3">
        <div>
          <div className="text-xs font-mono uppercase tracking-[0.25em] text-cyan-glow">Product {index + 1}</div>
          <p className="mt-1 text-sm text-slate-400">Enter the product information exactly as it should be recorded for the shipment.</p>
        </div>
        <button
          type="button"
          onClick={() => onRemove(product.id)}
          disabled={!canRemove}
          className="rounded-lg border border-white/10 px-3 py-1.5 text-xs font-semibold text-slate-300 hover:bg-white/5 disabled:cursor-not-allowed disabled:opacity-50"
          data-testid={`ns-remove-product-${index + 1}`}
        >
          Remove product
        </button>
      </div>

      <div className="grid grid-cols-1 gap-5 md:grid-cols-2">
        <Field
          label="Product name"
          value={product.product_name}
          onChange={onChange(product.id, 'product_name')}
          error={errors.product_name}
          testid={`ns-product-${index + 1}-name`}
          className="md:col-span-2"
        />
        <Select
          label="Product use"
          value={product.product_use}
          onChange={onChange(product.id, 'product_use')}
          options={PRODUCT_USE_OPTIONS}
          placeholder="Select product use"
          error={errors.product_use}
          testid={`ns-product-${index + 1}-use`}
        />
        <Select
          label="Organic"
          value={product.organic}
          onChange={onChange(product.id, 'organic')}
          options={ORGANIC_OPTIONS}
          placeholder="Select organic status"
          error={errors.organic}
          testid={`ns-product-${index + 1}-organic`}
        />
        <Field
          label="Quantity value"
          type="number"
          min="0.01"
          step="any"
          value={product.quantity_value}
          onChange={onChange(product.id, 'quantity_value')}
          error={errors.quantity_value}
          testid={`ns-product-${index + 1}-quantity-value`}
        />
        <Select
          label="Quantity unit"
          value={product.quantity_unit}
          onChange={onChange(product.id, 'quantity_unit')}
          options={QUANTITY_UNIT_OPTIONS}
          placeholder="Select quantity unit"
          error={errors.quantity_unit}
          testid={`ns-product-${index + 1}-quantity-unit`}
        />
        <Field
          label="HS code"
          value={product.hs_code}
          onChange={onHsCodeChange(product.id)}
          hint="Optional"
          helperText={selectedCandidateHsCode
            ? 'AI-suggested HS code selected. You can still edit it manually.'
            : ''}
          required={false}
          testid={`ns-product-${index + 1}-hs-code`}
          className="md:col-span-2"
        />
        <div className="md:col-span-2">
          <button
            type="button"
            onClick={() => onSuggestHsCode(product.id)}
            disabled={!product.product_name.trim() || product.hsSuggestionLoading}
            className="inline-flex items-center justify-center gap-2 rounded-lg border border-cyan-glow/30 px-4 py-2 text-sm font-semibold text-cyan-glow hover:bg-cyan-glow/10 disabled:cursor-not-allowed disabled:opacity-50"
            data-testid={`ns-product-${index + 1}-suggest-hs-code`}
          >
            {product.hsSuggestionLoading ? 'Suggesting...' : 'Suggest HS Code'}
          </button>
          {suggestionStatus ? (
            <div className={`mt-2 text-xs ${product.hsSuggestionError ? 'text-red-400' : 'text-slate-400'}`}>
              {suggestionStatus}
            </div>
          ) : null}
          {product.hsSuggestion ? (
            <div className="mt-3 space-y-3 rounded-lg border border-white/10 bg-navy-950/60 p-3">
              <div className="text-xs text-amber-200/90">
                {suggestionDisclaimer}
              </div>

              {Array.isArray(product.hsSuggestion.clarification_questions)
              && product.hsSuggestion.clarification_questions.length > 0 ? (
                <div>
                  <div className="text-[11px] font-mono uppercase tracking-[0.2em] text-slate-400">
                    Questions to improve classification
                  </div>
                  <ul className="mt-2 list-disc space-y-1 pl-4 text-xs text-slate-300">
                    {product.hsSuggestion.clarification_questions.map((question) => (
                      <li key={question}>{question}</li>
                    ))}
                  </ul>
                </div>
                ) : null}

              {Array.isArray(product.hsSuggestion.candidates) && product.hsSuggestion.candidates.length > 0 ? (
                <div>
                  <div className="text-[11px] font-mono uppercase tracking-[0.2em] text-cyan-glow">
                    AI-suggested HS code candidates
                  </div>
                  <div className="mt-2 space-y-2">
                    {product.hsSuggestion.candidates.map((candidate) => (
                      <div
                        key={`${product.id}-${candidate.hs_code}`}
                        className={`rounded-lg border p-3 ${
                          selectedCandidateHsCode === candidate.hs_code
                            ? 'border-cyan-glow/40 bg-cyan-glow/5'
                            : 'border-white/10 bg-navy-900/70'
                        }`}
                      >
                        <div className="flex items-start justify-between gap-3">
                          <div className="min-w-0">
                            <div className="text-sm font-semibold text-white font-mono">
                              {candidate.hs_code}
                            </div>
                            <div className="mt-1 text-sm text-slate-200">
                              {candidate.title}
                            </div>
                          </div>
                          <span
                            className={`shrink-0 rounded-full border px-2 py-0.5 text-[10px] font-mono uppercase tracking-[0.2em] ${
                              selectedCandidateHsCode === candidate.hs_code
                                ? 'border-cyan-glow/40 bg-cyan-glow/10 text-cyan-glow'
                                : candidate.confidence === 'high'
                                  ? 'border-emerald-400/30 bg-emerald-400/10 text-emerald-300'
                                  : candidate.confidence === 'medium'
                                    ? 'border-amber-400/30 bg-amber-400/10 text-amber-300'
                                    : 'border-slate-400/20 bg-slate-400/10 text-slate-300'
                            }`}
                          >
                            {selectedCandidateHsCode === candidate.hs_code ? 'Selected' : candidate.confidence}
                          </span>
                        </div>

                        <div className="mt-2 text-xs leading-relaxed text-slate-300">
                          {candidate.reasoning}
                        </div>

                        {Array.isArray(candidate.assumptions) && candidate.assumptions.length > 0 ? (
                          <div className="mt-2">
                            <div className="text-[10px] font-mono uppercase tracking-[0.2em] text-slate-500">
                              Assumptions
                            </div>
                            <ul className="mt-1 list-disc space-y-1 pl-4 text-xs text-slate-400">
                              {candidate.assumptions.map((assumption) => (
                                <li key={assumption}>{assumption}</li>
                              ))}
                            </ul>
                          </div>
                        ) : null}

                        {Array.isArray(candidate.risk_notes) && candidate.risk_notes.length > 0 ? (
                          <div className="mt-2">
                            <div className="text-[10px] font-mono uppercase tracking-[0.2em] text-slate-500">
                              Risk notes
                            </div>
                            <ul className="mt-1 list-disc space-y-1 pl-4 text-xs text-slate-400">
                              {candidate.risk_notes.map((riskNote) => (
                                <li key={riskNote}>{riskNote}</li>
                              ))}
                            </ul>
                          </div>
                        ) : null}

                        {Array.isArray(candidate.jurisdiction_codes) && candidate.jurisdiction_codes.length > 0 ? (
                          <div className="mt-2">
                            <div className="text-[10px] font-mono uppercase tracking-[0.2em] text-slate-500">
                              Route-specific codes
                            </div>
                            <div className="mt-2 space-y-2">
                              {candidate.jurisdiction_codes.map((jurisdictionCode) => (
                                <div
                                  key={`${candidate.hs_code}-${jurisdictionCode.country}-${jurisdictionCode.code_system}-${jurisdictionCode.code}`}
                                  className="rounded-md border border-white/5 bg-white/[0.02] p-2"
                                >
                                  <div className="flex flex-wrap items-center gap-2 text-xs">
                                    <span className="font-semibold text-slate-200">{jurisdictionCode.country}</span>
                                    <span className="font-mono text-slate-500">{jurisdictionCode.code_system}</span>
                                    <span className="font-mono text-cyan-glow">{jurisdictionCode.code}</span>
                                    {jurisdictionCode.confidence ? (
                                      <span className="rounded-full border border-white/10 px-2 py-0.5 font-mono uppercase tracking-[0.2em] text-[10px] text-slate-300">
                                        {jurisdictionCode.confidence}
                                      </span>
                                    ) : null}
                                  </div>
                                  {jurisdictionCode.usage ? (
                                    <div className="mt-1 text-[11px] font-mono uppercase tracking-[0.18em] text-slate-500">
                                      {jurisdictionCode.usage}
                                    </div>
                                  ) : null}
                                  {jurisdictionCode.note ? (
                                    <div className="mt-1 text-xs text-slate-400">
                                      {jurisdictionCode.note}
                                    </div>
                                  ) : null}
                                </div>
                              ))}
                            </div>
                          </div>
                        ) : null}

                        <div className="mt-3">
                          <button
                            type="button"
                            onClick={() => onSelectHsCandidate(product.id, candidate)}
                            className={`inline-flex items-center justify-center rounded-lg border px-3 py-1.5 text-xs font-semibold transition-colors ${
                              selectedCandidateHsCode === candidate.hs_code
                                ? 'border-cyan-glow/40 bg-cyan-glow/10 text-cyan-glow hover:bg-cyan-glow/15'
                                : 'border-white/10 text-slate-200 hover:bg-white/5'
                            }`}
                          >
                            {selectedCandidateHsCode === candidate.hs_code ? 'Selected' : 'Use this code'}
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              ) : null}
            </div>
          ) : null}
        </div>
      </div>
    </div>
  );
}

function Field({
  label,
  value,
  onChange,
  testid,
  error,
  className = '',
  hint,
  required = true,
  type = 'text',
  min,
  step,
  helperText,
}) {
  return (
    <label className={`block ${className}`.trim()}>
      <span className="flex items-center justify-between gap-3 text-xs text-slate-400 uppercase tracking-wider font-mono">
        <span>{label}</span>
        {hint ? <span className="text-[10px] tracking-[0.25em] text-slate-500">{hint}</span> : null}
      </span>
      <input
        type={type}
        value={value}
        onChange={onChange}
        required={required}
        min={min}
        step={step}
        className={`mt-1.5 w-full rounded-lg border px-3 py-2.5 text-sm text-white focus:outline-none focus:ring-2 ${
          error
            ? 'border-red-500/40 bg-red-500/5 focus:border-red-400 focus:ring-red-500/20'
            : 'border-white/10 bg-navy-950 focus:border-cyan-glow/50 focus:ring-cyan-glow/20'
        }`}
        data-testid={testid}
      />
      {error ? <div className="mt-1.5 text-xs text-red-400">{error}</div> : null}
      {!error && helperText ? <div className="mt-1.5 text-xs text-cyan-glow">{helperText}</div> : null}
    </label>
  );
}

function Select({
  label,
  value,
  onChange,
  options,
  testid,
  error,
  className = '',
  placeholder,
}) {
  return (
    <label className={`block ${className}`.trim()}>
      <span className="text-xs text-slate-400 uppercase tracking-wider font-mono">{label}</span>
      <select
        value={value}
        onChange={onChange}
        required
        className={`mt-1.5 w-full rounded-lg border px-3 py-2.5 text-sm text-white focus:outline-none focus:ring-2 ${
          error
            ? 'border-red-500/40 bg-red-500/5 focus:border-red-400 focus:ring-red-500/20'
            : 'border-white/10 bg-navy-950 focus:border-cyan-glow/50 focus:ring-cyan-glow/20'
        }`}
        data-testid={testid}
      >
        <option value="">{placeholder}</option>
        {options.map((option) => (
          <option key={option.value} value={option.value}>
            {option.label}
          </option>
        ))}
      </select>
      {error ? <div className="mt-1.5 text-xs text-red-400">{error}</div> : null}
    </label>
  );
}
